import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../../../config/api';
import { useTemplateAnalyticsMV } from '../../TemplateManagerDashboard/hooks/useTemplateAnalyticsMV';
import {
  SEARCH_MIN_CHARS,
  UI_DELAY_DEBOUNCE,
  MESSAGE_DURATION_SUCCESS,
} from '../../../config/constants';
import {
  sortTemplates,
  normalizeStatuses,
  getApiStatusFilter,
} from '../components/utils';

/**
 * Custom hook for managing template dashboard state and data.
 *
 * @returns {Object} Dashboard state and handlers
 */
export function useTemplateDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Pagination and filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(['active', 'draft']); // Default to active and draft
  const [sortBy, setSortBy] = useState('created_desc');
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Debounce search query
  useEffect(() => {
    if (searchQuery.length > 0 && searchQuery.length < SEARCH_MIN_CHARS) {
      setIsDebouncing(true);
      const timer = setTimeout(() => setIsDebouncing(false), UI_DELAY_DEBOUNCE);
      return () => clearTimeout(timer);
    } else {
      setIsDebouncing(false);
    }
  }, [searchQuery]);

  // Normalize status filter for API vs UI
  const normalizedStatuses = React.useMemo(
    () => normalizeStatuses(statusFilter),
    [statusFilter]
  );

  // For API calls, only pass status when a single concrete status is selected.
  const apiStatusFilter = React.useMemo(
    () => getApiStatusFilter(normalizedStatuses),
    [normalizedStatuses]
  );

  // Fetch templates with analytics using server-side pagination.
  const {
    templates: apiTemplates,
    loading,
    error,
    refetch,
    totalCount,
  } = useTemplateAnalyticsMV({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    category: categoryFilter !== 'all' ? categoryFilter : null,
    status: apiStatusFilter,
    searchQuery:
      searchQuery && searchQuery.trim().length >= SEARCH_MIN_CHARS
        ? searchQuery.trim()
        : null,
    enabled: true,
  });

  // Server-side filtering and pagination are now handled by the API
  // No need for client-side status filtering since backend supports multiple statuses
  // Apply client-side sorting only (if needed, though backend also supports sorting)
  const templates = React.useMemo(() => {
    return sortTemplates(apiTemplates, sortBy);
  }, [apiTemplates, sortBy]);

  // Calculate pagination info from API metadata
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const initialLoading = loading && !apiTemplates.length;

  // Refreshing state for manual refresh button
  const [refreshing, setRefreshing] = useState(false);

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [executionHistoryOpen, setExecutionHistoryOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [experimentDialogOpen, setExperimentDialogOpen] = useState(false);
  const [templateForExperiment, setTemplateForExperiment] = useState(null);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedTemplateForAnalytics, setSelectedTemplateForAnalytics] =
    useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedFeedbackTemplate, setSelectedFeedbackTemplate] =
    useState(null);
  const [experimentsDialogOpen, setExperimentsDialogOpen] = useState(false);
  const [selectedTemplateForExperiments, setSelectedTemplateForExperiments] =
    useState(null);

  // Execution history state
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);

  // Snackbar feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const closeSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  // Show an informational toast when filters return 0 results
  const emptyToastShownRef = useRef(false);
  useEffect(() => {
    const hasActiveFilters =
      (categoryFilter && categoryFilter !== 'all') ||
      (searchQuery && searchQuery.trim().length > 0);

    if (
      !loading &&
      templates.length === 0 &&
      hasActiveFilters &&
      !emptyToastShownRef.current
    ) {
      setSnackbar({
        open: true,
        message: 'No templates found for the selected filters',
        severity: 'info',
      });
      emptyToastShownRef.current = true;
    }

    // Reset the guard when results appear or filters clear
    if (templates.length > 0 || !hasActiveFilters) {
      emptyToastShownRef.current = false;
    }
  }, [loading, templates.length, categoryFilter, statusFilter, searchQuery]);

  // Success alert visibility
  const [successVisible, setSuccessVisible] = useState(true);

  // Auto-hide success message
  useEffect(() => {
    if (successVisible && templates.length > 0) {
      const timer = setTimeout(() => {
        setSuccessVisible(false);
      }, MESSAGE_DURATION_SUCCESS);
      return () => clearTimeout(timer);
    }
  }, [successVisible, templates.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reload when location changes (e.g., after creating a template)
  useEffect(() => {
    if (location.state?.refresh) {
      refetch();
      setCurrentPage(1); // Reset to first page
    }
  }, [location.state, refetch]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    setSnackbar({
      open: true,
      message: 'Refreshing templates…',
      severity: 'info',
    });
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  // Load execution history for a template
  const loadExecutionHistory = useCallback(async templateId => {
    if (!templateId) {
      return;
    }

    try {
      setExecutionsLoading(true);
      setExecutions([]);

      const params = new URLSearchParams({
        template_id: String(templateId),
        page: '1',
        page_size: '100',
      });

      const response = await apiFetch(
        `/api/v1/templates/executions/?${params.toString()}`
      );

      // Handle response structure
      if (response && response.success !== false) {
        if (Array.isArray(response)) {
          setExecutions(response);
        } else if (response.executions && Array.isArray(response.executions)) {
          setExecutions(response.executions);
        } else if (response.detail) {
          setExecutions([]);
        } else {
          setExecutions([]);
        }
      } else {
        setExecutions([]);
      }
    } catch (error) {
      setExecutions([]);
    } finally {
      setExecutionsLoading(false);
    }
  }, []);

  // Handle template deletion
  const handleDeleteTemplate = async templateId => {
    if (deleteLoading) return;

    setDeleteLoading(true);
    try {
      await apiFetch(`/api/v1/templates/${templateId}`, { method: 'DELETE' });
      refetch(); // Refresh the template list
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSnackbar({
        open: true,
        message: 'Template deleted successfully',
        severity: 'success',
      });
    } catch (err) {
      const errorMessage = err.message || err.toString() || '';
      if (
        errorMessage.includes('404') ||
        errorMessage.toLowerCase().includes('not found')
      ) {
        refetch();
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
        setSnackbar({
          open: true,
          message:
            'Template was already deleted, cache invalidated and list refreshed',
          severity: 'info',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Error: ${err.message || 'Failed to delete template'}`,
          severity: 'error',
        });
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // Pagination handlers
  const goToPage = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const changePageSize = useCallback(newSize => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing size
  }, []);

  // Reset pagination when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, statusFilter, searchQuery, sortBy]);

  // Open delete confirmation dialog
  const openDeleteDialog = template => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const openExecutionHistory = async template => {
    setSelectedTemplate(template);
    setExecutionHistoryOpen(true);
    await loadExecutionHistory(template.id || template.template_id);
  };

  // Handle template editing
  const handleEdit = template => {
    const templateId = template.id || template.template_id;
    navigate(`/templates/${templateId}/edit`, {
      state: { from: '/templates' },
    });
  };

  // Handle creating new template
  const handleCreateTemplate = () => {
    navigate('/templates/new', { state: { from: '/templates' } });
  };

  // Handle experiment creation
  const handleViewExperiments = template => {
    setSelectedTemplateForExperiments({
      id: template.id || template.template_id,
      name: template.template_name || template.name,
    });
    setExperimentsDialogOpen(true);
  };

  const handleExperimentCreated = experimentData => {
    setExperimentDialogOpen(false);
    setTemplateForExperiment(null);
    setSnackbar({
      open: true,
      message: 'Experiment created successfully',
      severity: 'success',
    });
    navigate('/experiments');
  };

  // Template card action handlers
  const handleExecute = useCallback(
    template => {
      navigate(`/templates/${template.id || template.template_id}/execute`, {
        state: { from: '/templates' },
      });
    },
    [navigate]
  );

  const handleOpenVersions = useCallback(
    template => {
      navigate(`/templates/${template.id || template.template_id}/versions`, {
        state: { from: '/templates' },
      });
    },
    [navigate]
  );

  const handleViewAnalytics = useCallback(template => {
    setSelectedTemplateForAnalytics({
      id: template.id || template.template_id,
      name: template.template_name || template.name,
    });
    setAnalyticsDialogOpen(true);
  }, []);

  const handleOpenFeedback = useCallback(template => {
    setSelectedFeedbackTemplate(template);
    setFeedbackDialogOpen(true);
  }, []);

  return {
    // State
    templates,
    loading,
    error,
    initialLoading,
    refreshing,
    totalPages,
    totalCount,
    currentPage,
    pageSize,
    searchQuery,
    categoryFilter,
    statusFilter,
    sortBy,
    isDebouncing,
    successVisible,
    setSuccessVisible,
    // Dialog state
    deleteDialogOpen,
    setDeleteDialogOpen,
    templateToDelete,
    setTemplateToDelete,
    deleteLoading,
    executionHistoryOpen,
    setExecutionHistoryOpen,
    selectedTemplate,
    experimentDialogOpen,
    setExperimentDialogOpen,
    templateForExperiment,
    setTemplateForExperiment,
    analyticsDialogOpen,
    setAnalyticsDialogOpen,
    selectedTemplateForAnalytics,
    setSelectedTemplateForAnalytics,
    feedbackDialogOpen,
    setFeedbackDialogOpen,
    selectedFeedbackTemplate,
    setSelectedFeedbackTemplate,
    experimentsDialogOpen,
    setExperimentsDialogOpen,
    selectedTemplateForExperiments,
    setSelectedTemplateForExperiments,
    executions,
    executionsLoading,
    snackbar,
    closeSnackbar,
    // Handlers
    refetch,
    handleRefresh,
    handleDeleteTemplate,
    handleEdit,
    handleCreateTemplate,
    handleViewExperiments,
    handleExperimentCreated,
    handleExecute,
    handleOpenVersions,
    handleViewAnalytics,
    handleOpenFeedback,
    openDeleteDialog,
    openExecutionHistory,
    goToPage,
    changePageSize,
    // Setters
    setSearchQuery,
    setCategoryFilter,
    setStatusFilter,
    setSortBy,
  };
}
